import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { ArrowLeft, UserX, ArrowRight, Calendar as CalendarIcon } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUT_LABELS = {
    nouveau: { label: 'Nouveau', color: 'bg-blue-100 text-blue-700 border border-blue-200' },
    sans_reponse_1: { label: 'Sans réponse 1', color: 'bg-yellow-100 text-yellow-700 border border-yellow-200' },
    sans_reponse_2: { label: 'Sans réponse 2', color: 'bg-amber-100 text-amber-700 border border-amber-200' },
    sans_reponse_3: { label: 'Sans réponse 3', color: 'bg-orange-100 text-orange-700 border border-orange-200' },
    relance_whatsapp: { label: 'Sans réponse WhatsApp', color: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
};

function formatDate(iso) {
    if (!iso) return '-';
    try {
        return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch { return '-'; }
}

export default function ProspectsSansReponse() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetch = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/dashboard/prospects-sans-reponse`, { withCredentials: true });
                setItems(res.data);
            } catch {
                // endpoint may not exist yet
            } finally {
                setLoading(false);
            }
        };
        fetch();
    }, []);

    return (
        <Layout>
            <div className="space-y-6" data-testid="prospects-sans-reponse-page">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate('/')} className="flex items-center gap-1 text-gray-500 hover:text-[#1B365D] transition-colors text-sm" data-testid="psr-back-btn">
                        <ArrowLeft className="w-4 h-4" /> Retour
                    </button>
                    <h1 className="text-2xl font-bold text-[#1B365D]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Prospects sans reponse
                    </h1>
                </div>

                <Card className="shadow-sm">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-lg font-semibold text-[#1B365D] flex items-center gap-2">
                            <UserX className="w-5 h-5 text-[#C9A962]" />
                            Prospects sans reponse
                            <Badge className="bg-gray-100 text-gray-600 border border-gray-200 text-xs ml-1">{items.length}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1B365D]" />
                            </div>
                        ) : items.length === 0 ? (
                            <div className="flex flex-col items-center justify-center py-12 text-center">
                                <div className="h-16 w-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
                                    <UserX className="h-8 w-8 text-gray-400" />
                                </div>
                                <p className="text-gray-500 font-medium">Aucun prospect sans reponse</p>
                                <p className="text-gray-400 text-sm mt-1">Les prospects sans reponse apparaitront ici automatiquement</p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {items.map((p, i) => {
                                    const sc = STATUT_LABELS[p.statut] || { label: p.statut, color: 'bg-gray-100 text-gray-700 border border-gray-200' };
                                    return (
                                    <div key={p.id || i}
                                        onClick={() => navigate(`/prospects/${p.id}`)}
                                        className="flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:border-purple-200 hover:bg-purple-50/30 cursor-pointer transition-all group"
                                        data-testid={`psr-${i}`}>
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center shrink-0">
                                                <UserX className="w-3.5 h-3.5 text-purple-600" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-semibold text-gray-800 truncate group-hover:text-[#1B365D]">{p.nom}</p>
                                                <div className="flex items-center gap-3 text-xs text-gray-400 flex-wrap mt-0.5">
                                                    <span className="flex items-center gap-1">
                                                        <CalendarIcon className="w-3 h-3" /> Créé le {formatDate(p.created_at)}
                                                    </span>
                                                    {p.email || p.telephone ? <span>{p.email || p.telephone}</span> : null}
                                                </div>
                                                {p.last_remarque ? (
                                                    <div className="mt-1.5 flex items-start gap-1.5 text-xs">
                                                        <span className="text-gray-400 shrink-0">Remarque :</span>
                                                        <span className="text-gray-700 italic line-clamp-2"
                                                            title={p.last_remarque}
                                                            data-testid={`psr-remarque-${i}`}>
                                                            « {p.last_remarque} »
                                                        </span>
                                                    </div>
                                                ) : null}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <Badge className={`${sc.color} text-[10px]`}>{sc.label}</Badge>
                                            <ArrowRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-purple-500 transition-colors" />
                                        </div>
                                    </div>
                                    );
                                })}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </Layout>
    );
}
